import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TextField, InputAdornment, TextFieldProps } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

export interface SearchInputHandle {
  /** 現在の入力値を取得 (検索ボタンクリック時に使用) */
  getValue: () => string;
  /** 入力値を強制的にクリア */
  clear: () => void;
}

interface SearchInputProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'ref'> {
  /**
   * 入力の初期値. URL から復元した値などを渡す.
   * 外部で initialValue が変わった場合は内部状態を同期する.
   */
  initialValue?: string;

  /**
   * Enter キー入力時に呼ばれる. ボタンの onClick からも getValue() で値を取得して同じ処理を行う.
   */
  onCommit?: (value: string) => void;

  /**
   * focus が外れた時にも commit する場合 true.
   * 検索ボタンがないページ (client-side filter) で使用.
   */
  commitOnBlur?: boolean;

  /**
   * 検索アイコンを startAdornment として表示するか (default: true)
   */
  showSearchIcon?: boolean;
}

/**
 * 検索キーワード入力コンポーネント.
 *
 * 入力値を内部 state に閉じ込めて、親コンポーネントの再レンダリングを防止する.
 * これにより 1300+ 行の親コンポーネント (ProductManagement 等) でもタイピングが
 * スムーズになる.
 *
 * 親は ref から getValue() で値を取得し、検索ボタン onClick で使用する.
 *
 * @example
 * ```tsx
 * const ref = useRef<SearchInputHandle>(null);
 * const handleSearch = (keyword: string) => {
 *   setSearchParams({ ...searchParams, keyword });
 *   syncToUrl({ keyword });
 *   fetchData();
 * };
 *
 * <SearchInput
 *   ref={ref}
 *   initialValue={urlParams.get('keyword') || ''}
 *   onCommit={handleSearch}
 *   placeholder="検索..."
 *   size="small"
 * />
 * <Button onClick={() => handleSearch(ref.current?.getValue() || '')}>
 *   検索
 * </Button>
 * ```
 */
const SearchInput = forwardRef<SearchInputHandle, SearchInputProps>(
  ({ initialValue = '', onCommit, commitOnBlur, showSearchIcon = true, InputProps, onKeyPress, onBlur, ...props }, ref) => {
    const [value, setValue] = useState(initialValue);
    const valueRef = useRef(value);
    valueRef.current = value;

    useImperativeHandle(ref, () => ({
      getValue: () => valueRef.current,
      clear: () => setValue(''),
    }), []);

    // 外部 initialValue が変わったら内部 state を同期
    // (URL 戻る時に keyword=xxx が変わった場合など)
    const prevInitialRef = useRef(initialValue);
    useEffect(() => {
      if (prevInitialRef.current !== initialValue) {
        setValue(initialValue);
        prevInitialRef.current = initialValue;
      }
    }, [initialValue]);

    return (
      <TextField
        {...props}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && onCommit) {
            onCommit(valueRef.current);
          }
          onKeyPress?.(e);
        }}
        onBlur={(e) => {
          if (commitOnBlur && onCommit) {
            onCommit(valueRef.current);
          }
          onBlur?.(e);
        }}
        InputProps={{
          ...InputProps,
          startAdornment: showSearchIcon
            ? (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1.1rem' }} />
              </InputAdornment>
            )
            : InputProps?.startAdornment,
        }}
      />
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
